/**
 * Lightweight runtime validation for module/form schemas (AC-34).
 * Soft-fail: returns a typed error instead of throwing; callers decide UX.
 */

export type SchemaValidationKind = 'module' | 'form' | 'module-list';

export class SchemaValidationError extends Error {
  readonly kind: SchemaValidationKind;
  readonly path: string;
  readonly code: string;

  constructor(kind: SchemaValidationKind, path: string, code: string, message: string) {
    super(message);
    this.name = 'SchemaValidationError';
    this.kind = kind;
    this.path = path;
    this.code = code;
  }
}

export interface SchemaValidationResult {
  ok: boolean;
  errors: SchemaValidationError[];
}

function err(kind: SchemaValidationKind, path: string, code: string, message: string): SchemaValidationError {
  return new SchemaValidationError(kind, path, code, message);
}

/** Validate a single module schema for required fields. */
export function validateModuleSchema(raw: unknown, index = 0): SchemaValidationResult {
  const errors: SchemaValidationError[] = [];
  const base = `modules[${index}]`;
  if (raw == null || typeof raw !== 'object') {
    errors.push(err('module', base, 'type', 'Module schema must be an object'));
    return {ok: false, errors};
  }
  const m = raw as Record<string, unknown>;
  if (m.id_number == null || m.id_number === '') {
    errors.push(err('module', `${base}.id_number`, 'required', 'id_number is required'));
  }
  if (m.name == null || m.name === '') {
    errors.push(err('module', `${base}.name`, 'required', 'name is required'));
  }
  if (m.type == null || m.type === '') {
    errors.push(err('module', `${base}.type`, 'required', 'type is required'));
  }
  return {ok: errors.length === 0, errors};
}

/** Validate a list of module schemas. */
export function validateModuleSchemaList(raw: unknown): SchemaValidationResult {
  if (!Array.isArray(raw)) {
    return {
      ok: false,
      errors: [err('module-list', 'modules', 'type', 'Module schema list must be an array')],
    };
  }
  const errors: SchemaValidationError[] = [];
  raw.forEach((item, i) => {
    errors.push(...validateModuleSchema(item, i).errors);
  });
  return {ok: errors.length === 0, errors};
}

/** Validate a form schema for required top-level fields. */
export function validateFormSchema(raw: unknown): SchemaValidationResult {
  const errors: SchemaValidationError[] = [];
  if (raw == null || typeof raw !== 'object') {
    errors.push(err('form', 'form', 'type', 'Form schema must be an object'));
    return {ok: false, errors};
  }
  const f = raw as Record<string, unknown>;
  if (f.properties == null || typeof f.properties !== 'object') {
    errors.push(err('form', 'form.properties', 'required', 'properties is required'));
  }
  return {ok: errors.length === 0, errors};
}

/** Soft-validate and return the first error (or null when valid). */
export function firstSchemaError(
  result: SchemaValidationResult,
): SchemaValidationError | null {
  return result.errors[0] ?? null;
}
